import { useEffect, useRef, useState } from 'react';
import { GrStorage } from 'react-icons/gr';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { apiEndPoints } from '../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';
import { Drawer } from '../Drawer';
import Input from '../input';
import { Label, Tooltip } from 'flowbite-react';
import { BsInfoCircle } from 'react-icons/bs';
import { tierList } from '../../pages/users/pages/bucketStorages';
import { NavLink } from 'react-router-dom';

// ─── Helpers (mirrors BucketStorages.jsx) ────────────────────────────────────

function formatPrice(amount) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Tier Card ────────────────────────────────────────────────────────────────

function TierCard({ tier, selected, onSelect }) {
  const isSelected = selected?.name === tier.name;

  return (
    <button
      type="button"
      onClick={() => onSelect(tier)}
      className={`
        relative w-full rounded-xl border-2 p-4 text-left transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-[#1da1f1]/40
        ${
          isSelected
            ? 'border-[#1da1f1] bg-[#1da1f1]/5 shadow-md'
            : 'border-gray-200 bg-white hover:border-[#1da1f1]/50 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-[#1da1f1]/50'
        }
      `}
    >
      {isSelected && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#1da1f1]">
          <svg
            className="h-3 w-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </span>
      )}

      <p
        className={`text-sm font-semibold ${isSelected ? 'text-[#1da1f1]' : 'text-gray-700 dark:text-gray-200'}`}
      >
        {tier.name}
      </p>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {tier.gb} GB storage
      </p>
      <div className="mt-2">
        <span
          className={`text-lg font-bold ${isSelected ? 'text-[#1da1f1]' : 'text-gray-800 dark:text-gray-100'}`}
        >
          {formatPrice(tier.monthlyPrice)}
        </span>
        <span className="ml-1 text-xs text-gray-400">/month</span>
      </div>
    </button>
  );
}

// ─── BucketItem ───────────────────────────────────────────────────────────────

function BucketItem({ item, projectId }) {
  const [open, setOpen] = useState(false);
  const [editBucket, setEditBucket] = useState(item);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const menuRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await apiEndPoints.deleteBucketStorage(
        projectId,
        deleteTarget,
      );
      setDeleteTarget(null);
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting bucket');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetEditChange = (e) => {
    const { name, type, value, checked } = e.target;
    setEditBucket((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEditBucket = async (e) => {
    e.preventDefault();
    setIsEditing(true);
    try {
      await apiEndPoints.updateBucketStorage(projectId, {
        name: editBucket.name,
        public: !editBucket.private,
        tier: selectedTier?.name,
        price: selectedTier?.monthlyPrice,
      });
      window.location.reload();
      setEditBucket(item);
      toast.success(`${editBucket.name} bucket updated successfully`);
      setIsEditOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating bucket');
    } finally {
      setIsEditing(false);
    }
  };

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <li className="group relative rounded-md border border-gray-200 bg-white p-4 hover:border-blue-400 hover:shadow-sm">
      <div className="flex items-center gap-3">
        <GrStorage
          className="h-6 w-6 text-gray-500 group-hover:text-blue-600"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">
            {item.name}
          </p>
          {typeof item.public === 'boolean' ? (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                item.public
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {item.public ? 'Public' : 'Private'}
            </span>
          ) : null}
        </div>
      </div>

      {/* Three dots button */}
      <div className="pointer-events-none absolute bottom-2 right-2">
        <button
          type="button"
          className="pointer-events-auto inline-flex items-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <HiOutlineDotsVertical className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </button>
      </div>

      {/* Dropdown menu */}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Bucket actions"
          className="absolute bottom-10 right-2 z-10 w-40 origin-bottom-right rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none"
        >
          <button
            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            role="menuitem"
            autoFocus
            onClick={() => {
              setEditBucket(item);
              setIsEditOpen(true);
            }}
          >
            Edit
          </button>
          <NavLink
            to={`/resources/${projectId}/bucket-storages/${encodeURIComponent(item.name)}`}
            state={{ public: item.public, created: item.created }}
          >
            <button
              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Details
            </button>
          </NavLink>
          <button
            className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setDeleteTarget(item.name);
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      )}

      {/* Edit Drawer */}
      <Drawer
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title={`Edit ${item.name} details`}
      >
        <form className="space-y-5" onSubmit={handleEditBucket}>
          {/* Bucket name (read-only) */}
          <Input
            label="Bucket Name"
            type="text"
            value={editBucket.name}
            readonly
          />

          {/* Tier cards */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Select a Tier
            </p>
            <div className="flex flex-col gap-2">
              {tierList.map((tier) => (
                <TierCard
                  key={tier.name}
                  tier={tier}
                  selected={selectedTier}
                  onSelect={setSelectedTier}
                />
              ))}
            </div>
          </div>

          {/* Price summary */}
          {selectedTier && (
            <div className="rounded-lg border border-[#1da1f1]/30 bg-[#1da1f1]/5 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total (Monthly)</p>
                  <p className="text-base font-bold text-[#1da1f1]">
                    {formatPrice(selectedTier.monthlyPrice)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {selectedTier.name} · {selectedTier.gb} GB
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Private checkbox */}
          <div className="flex items-center gap-2">
            <Label
              htmlFor="private"
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                id="private"
                name="private"
                className="text-primaryBlue rounded-sm"
                checked={editBucket.private}
                onChange={handleSetEditChange}
              />
              Private
            </Label>
            <Tooltip content="Set to public by default" placement="right">
              <BsInfoCircle
                size={10}
                className="cursor-pointer text-gray-400"
              />
            </Tooltip>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setEditBucket(item);
                setIsEditOpen(false);
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isEditing}
              className="rounded bg-[#1da1f1] px-3 py-2 text-sm text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
            >
              {isEditing ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Drawer>

      {/* Delete confirmation */}
      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete Bucket"
        name={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        description="To confirm deletion, type the bucket name exactly"
        isDeleting={isDeleting}
      />
    </li>
  );
}

export default BucketItem;
